import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import {
  applyOrderAction,
  exportManagerOrders,
  getOrder,
  listManagerOrders,
} from '../api/panel-api';
import { formatDateTime } from '../data/panel';

const FILTER_OPTIONS = [
  { value: 'exclude', label: 'Выкл' },
  { value: 'all', label: 'В списке' },
  { value: 'only', label: 'Только' },
];

const STATUS_LABEL = {
  N: 'Оформлен',
};

function money(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

function countText(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Math.round(n).toLocaleString('ru-RU').replace(/\u00A0/g, ' ');
}

function clientLabel(client) {
  if (!client) return '';
  if (client.user_type === 'legal_entity' && client.company_name) return client.company_name;
  const name = `${client.surname || ''} ${client.name || ''}`.trim();
  return name || client.company_name || '';
}

function statusLabel(code) {
  if (!code) return '';
  return STATUS_LABEL[code] || code;
}

function statusTone(label) {
  if (label === 'Выполнен') return 'success';
  if (label === 'Отменён' || label === 'Отменен') return 'neutral';
  if (label === 'Оформлен' || label === 'Ожидает оплаты') return 'warning';
  return 'info';
}

function accountStatusIcon(item) {
  if (!item || !item.client) return '';
  return 'person';
}

function orderManagerLabel(item, card) {
  const source = item.placed_cs_manager
    || (card && card.placed_cs_manager)
    || item.manager;
  if (source && typeof source === 'object') {
    const name = `${source.last_name || ''} ${source.first_name || ''}`.trim();
    if (name) return name;
  }
  if (typeof source === 'string' && source.trim()) return source.trim();
  const fallback = `${item.placed_rhm_last_name || ''} ${item.placed_rhm_first_name || ''}`.trim();
  return fallback || '—';
}

function needsCard(item) {
  return Boolean(item.legal_entity_id) && item.status === 'N';
}

function OrderBadge({ code }) {
  const label = statusLabel(code);
  if (!label) return null;
  return <span className={`order-badge order-badge--${statusTone(label)}`}>{label}</span>;
}

function SortHead({ label, field, sort, order, onSort }) {
  const active = sort === field;
  return (
    <button
      type="button"
      className={active && order === 'desc' ? 'sort is-desc' : 'sort'}
      onClick={() => onSort(field)}
    >
      {label}
      {active ? <Icon name="arrow-up" size={14} /> : null}
    </button>
  );
}

function ManagerOrders({ managerId, role, readOnly = false }) {
  const canExport = !readOnly && (role === 'administrator' || role === 'cs_employee');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [sort, setSort] = useState('ordered_at');
  const [order, setOrder] = useState('desc');
  const [ozm, setOzm] = useState('all');
  const [legalEntity, setLegalEntity] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cards, setCards] = useState({});
  const [dialog, setDialog] = useState(null);
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listManagerOrders({
      id: managerId,
      page,
      perPage: pageSize,
      sort,
      order,
      ozm,
      legalEntity,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        setError('');
      })
      .catch((loadError) => {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [managerId, page, pageSize, sort, order, ozm, legalEntity]);

  useEffect(() => {
    const pendingIds = items.filter(needsCard).map((item) => item.id);
    if (!pendingIds.length) {
      setCards({});
      return undefined;
    }
    let cancelled = false;
    Promise.all(pendingIds.map((id) => getOrder(id).then((card) => [id, card]).catch(() => [id, null])))
      .then((pairs) => {
        if (cancelled) return;
        const next = {};
        pairs.forEach(([id, card]) => {
          next[id] = card;
        });
        setCards(next);
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  const filtersDefault = ozm === 'all' && legalEntity === 'all';
  const emptyAll = !loading && !error && total === 0 && filtersDefault;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  function changeSort(field) {
    if (sort === field) {
      setOrder((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSort(field);
    setOrder(field === 'number' ? 'asc' : 'desc');
  }

  function changeFilter(kind, value) {
    if (kind === 'ozm') setOzm(value);
    else setLegalEntity(value);
    setPage(1);
  }

  async function copyPay(url) {
    try {
      await navigator.clipboard.writeText(url);
      setToast({ title: 'Ссылка на оплату скопирована', lead: 'Отправьте её клиенту' });
    } catch (copyError) {
      setError(copyError.message || 'Не удалось скопировать ссылку');
    }
  }

  async function exportCsv() {
    setError('');
    try {
      const file = await exportManagerOrders(managerId);
      const href = URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (exportError) {
      setError(exportError.message);
    }
  }

  async function confirmOrder(item) {
    setPending(true);
    setError('');
    try {
      await applyOrderAction(item.id, 'confirm');
      setToast({ title: `Заказ ${item.number} подтверждён` });
      const data = await listManagerOrders({
        id: managerId,
        page,
        perPage: pageSize,
        sort,
        order,
        ozm,
        legalEntity,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setPending(false);
    }
  }

  async function submitDialog() {
    if (!dialog || !comment.trim()) return;
    setPending(true);
    setError('');
    try {
      await applyOrderAction(dialog.item.id, dialog.action, { comment: comment.trim() });
      setDialog(null);
      setComment('');
      const data = await listManagerOrders({
        id: managerId,
        page,
        perPage: pageSize,
        sort,
        order,
        ozm,
        legalEntity,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <div className="manager-tab__head">
        <h2>Заказы</h2>
        {!emptyAll ? (
          <div className="manager-tab__orders-tools">
            <div className="manager-tab__filter manager-tab__filter--order">
              <SelectField label="ОЗМ" required={false} value={ozm} onChange={(value) => changeFilter('ozm', value)} options={FILTER_OPTIONS} />
            </div>
            <div className="manager-tab__filter manager-tab__filter--order">
              <SelectField label="ОЗЮЛ" required={false} value={legalEntity} onChange={(value) => changeFilter('legal', value)} options={FILTER_OPTIONS} />
            </div>
            {canExport ? (
              <button type="button" className="btn btn--secondary" onClick={exportCsv}>
                <Icon name="download" size={16} />
                Экспорт
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {error ? <p className="empty">{error}</p> : null}
      {emptyAll ? (
        <div className="empty managers-empty managers-empty--tab">
          <div className="empty__icon"><Icon name="cart" size={24} /></div>
          <div>
            <p className="empty__title">Заказов пока нет</p>
            <p className="empty__lead">Здесь появятся заказы, относящиеся к сотруднику.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="manager-orders">
            <div className="manager-orders__pinned">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>
                      <SortHead label="Номер заказа" field="number" sort={sort} order={order} onSort={changeSort} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const card = cards[item.id];
                    const actions = card && card.actions;
                    const tall = !readOnly && (Boolean(item.payment_url) || Boolean(actions && (actions.confirm || actions.reject || actions.adjust || (card && card.shipment_blocked))));
                    return (
                      <Fragment key={item.id}>
                        <tr style={{ height: 44 }}>
                          <td>
                            <span className="order-number">
                              {item.number}
                              <span className="order-number__eye" aria-hidden="true">
                                <Icon name="eye" size={16} />
                              </span>
                            </span>
                          </td>
                        </tr>
                        {tall ? <tr className="order-row__pad" style={{ height: 56 }}><td /></tr> : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="manager-orders__main">
              <div className="manager-orders__edge manager-orders__edge--left" />
              <div className="manager-orders__viewport">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>
                        <SortHead label="Дата" field="ordered_at" sort={sort} order={order} onSort={changeSort} />
                      </th>
                      <th style={{ width: 140 }}>
                        <SortHead label="Статус заказа" field="status" sort={sort} order={order} onSort={changeSort} />
                      </th>
                      <th style={{ width: 180 }}>Клиент</th>
                      <th style={{ width: 70 }}>Статус аккаунта</th>
                      <th style={{ width: 80 }}>Товаров</th>
                      <th style={{ width: 100 }}>Сумма, ₽</th>
                      <th style={{ width: 90 }}>Скидки, ₽</th>
                      <th style={{ width: 90 }}>Доставка, ₽</th>
                      <th style={{ width: 120 }}>
                        <SortHead label="Итого, ₽" field="total" sort={sort} order={order} onSort={changeSort} />
                      </th>
                      <th style={{ width: 70 }}>ОЗМ</th>
                      <th style={{ width: 190 }}>ОЗЮЛ</th>
                      <th style={{ width: 190 }}>Менеджер</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const stamp = formatDateTime(item.ordered_at);
                      const card = cards[item.id];
                      const actions = card && card.actions;
                      const blocked = Boolean(card && card.shipment_blocked);
                      const showLegal = !readOnly && Boolean(actions && (actions.reject || actions.adjust || actions.confirm || blocked));
                      const tall = !readOnly && (Boolean(item.payment_url) || showLegal);
                      return (
                        <Fragment key={item.id}>
                          <tr style={{ height: 44 }}>
                            <td>
                              {stamp.date ? (
                                <span className="stamp">
                                  <span>{stamp.date}</span>
                                  <span>{stamp.time}</span>
                                </span>
                              ) : null}
                            </td>
                            <td><OrderBadge code={item.status} /></td>
                            <td>{clientLabel(item.client)}</td>
                            <td className="order-account">
                              {accountStatusIcon(item) ? <Icon name={accountStatusIcon(item)} size={18} /> : null}
                            </td>
                            <td>{countText(item.items_count)}</td>
                            <td>{money(item.goods_sum)}</td>
                            <td>{money(item.discount_sum)}</td>
                            <td>{money(item.delivery_sum)}</td>
                            <td>{money(item.total)}</td>
                            <td>{item.ozm ? <Icon name="person" size={20} /> : '—'}</td>
                            <td>
                              {item.legal_entity_id ? (
                                <span className="stamp">
                                  <span>{item.legal_entity_name || '—'}</span>
                                  {item.legal_entity_inn ? <span>ИНН {item.legal_entity_inn}</span> : null}
                                </span>
                              ) : '—'}
                            </td>
                            <td>{orderManagerLabel(item, card)}</td>
                          </tr>
                          {tall ? (
                            <tr className="order-row__actions" style={{ height: 56 }}>
                              <td colSpan={12}>
                                {item.payment_url ? (
                                  <button type="button" className="btn btn--secondary btn--small" onClick={() => copyPay(item.payment_url)}>
                                    <Icon name="copy" size={16} />
                                    Скопировать ссылку на оплату
                                  </button>
                                ) : null}
                                {showLegal ? (
                                  <>
                                    {actions.reject ? (
                                      <button
                                        type="button"
                                        className="btn btn--danger btn--small"
                                        disabled={pending}
                                        onClick={() => { setDialog({ action: 'reject', item }); setComment(''); }}
                                      >
                                        Отказать
                                      </button>
                                    ) : null}
                                    {actions.adjust ? (
                                      <button
                                        type="button"
                                        className="btn btn--secondary btn--small"
                                        disabled={pending}
                                        onClick={() => { setDialog({ action: 'adjust', item }); setComment(''); }}
                                      >
                                        Корректировка
                                      </button>
                                    ) : null}
                                    {actions.confirm || blocked ? (
                                      <button
                                        type="button"
                                        className="btn btn--small"
                                        disabled={pending || !actions.confirm}
                                        onClick={() => confirmOrder(item)}
                                      >
                                        Подтвердить
                                      </button>
                                    ) : null}
                                    {blocked && card.shipment_block_reason ? (
                                      <span className="order-block">{card.shipment_block_reason}</span>
                                    ) : null}
                                  </>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="manager-orders__edge manager-orders__edge--right" />
            </div>
          </div>
          {total ? (
            <footer className="pager">
              <label className="pager__size">
                Показывать по
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <div className="pager__nav">
                <span>{`${from}–${to} из ${total}`}</span>
                <button type="button" className="icon-button" aria-label="Предыдущая страница" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>
                  <Icon name="chevron-left" size={16} />
                </button>
                <span className="counter counter--page">{page}</span>
                <button type="button" className="icon-button" aria-label="Следующая страница" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>
                  <Icon name="chevron-right" size={16} />
                </button>
              </div>
            </footer>
          ) : null}
        </>
      )}
      {toast ? createPortal(
        <div className="toast" role="status">
          <span className="toast__icon">
            <Icon name="check-circle" size={18} tone="inherit" />
          </span>
          <div className="toast__copy">
            <p className="toast__title">{toast.title}</p>
            {toast.lead ? <p className="toast__lead">{toast.lead}</p> : null}
          </div>
          <button type="button" className="toast__close" aria-label="Закрыть" onClick={() => setToast(null)}>
            <Icon name="x" size={14} tone="inherit" />
          </button>
        </div>,
        document.body,
      ) : null}
      {dialog ? createPortal(
        <div className="overlay overlay--front">
          <div className="dialog" role="dialog" aria-modal="true">
            <div className="dialog__head">
              <h2>{dialog.action === 'reject' ? 'Отказать в заказе' : 'Отправить на корректировку'}</h2>
              <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => setDialog(null)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="dialog__body">
              <div className="who">
                <strong>{`Заказ ${dialog.item.number}`}</strong>
                <span>
                  {[dialog.item.legal_entity_name, money(dialog.item.total) ? `${money(dialog.item.total)} ₽` : '']
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
              <textarea
                className="comment"
                placeholder="Комментарий *"
                maxLength={1000}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
              <div className="note note--warning">
                <Icon name="alert" size={20} />
                <p>
                  {dialog.action === 'reject'
                    ? 'Заказ отменится. Клиент получит уведомление об отказе.'
                    : 'Заказ отменится. Все позиции вернутся в корзину клиента, подарки и акционные товары не переносятся. Клиент получит уведомление об отказе.'}
                </p>
              </div>
            </div>
            <div className="dialog__footer">
              <button type="button" className="btn btn--secondary btn--small" onClick={() => setDialog(null)}>Отмена</button>
              <button
                type="button"
                className={dialog.action === 'reject' ? 'btn btn--danger btn--small' : 'btn btn--small'}
                disabled={pending || !comment.trim()}
                onClick={submitDialog}
              >
                {dialog.action === 'reject' ? 'Отказать' : 'На корректировку'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export default ManagerOrders;
